import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Card,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Box,
  Typography,
  Paper,
  Checkbox,
  Button,
} from "@mui/material";
import PaymentTableRow from "../PaymentTableRow";
import PaymentDrawer from "../PaymentDrawer";

export default function AllStatusTable({
  data = [],
  loading = false,
  onLinkCopy,
  onDelete,
  onApprove,
  isAdmin,
  overdueOnly = false,
  isSuperUser = false,
  onMarkUnderProcess,
  onRevertToWaiting,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Selection is scoped to whatever's currently on screen — a fresh fetch
  // (new page, search, or filter) clears it rather than carrying stale
  // picks (and their contribution to the total below) into an unrelated
  // result set.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [data]);

  // Stable reference so toggling one row's checkbox doesn't force every
  // *other* row's memoized PaymentTableRow to re-render too.
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Only Waiting rows can ever be queued for processing (enforced again
  // server-side via status:"W" in updateWaitingPaymentLinks), so "select
  // all" and its checked/indeterminate state must only consider those —
  // otherwise Pending/Applied/etc. rows would show as selectable with no
  // way to actually act on them.
  const waitingItems = useMemo(() => data.filter((item) => item.status === "W"), [data]);
  const allSelected = waitingItems.length > 0 && waitingItems.every((item) => selectedIds.has(item._id));
  const someSelected = !allSelected && waitingItems.some((item) => selectedIds.has(item._id));

  // Selects every Waiting row currently on screen, or clears the selection
  // if all of them already are — standard "select all" toggle semantics.
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allCurrentlySelected = waitingItems.length > 0 && waitingItems.every((item) => prev.has(item._id));
      return allCurrentlySelected ? new Set() : new Set(waitingItems.map((item) => item._id));
    });
  }, [waitingItems]);

  const selectedPayableTotal = useMemo(
    () =>
      data.reduce(
        (sum, item) => (selectedIds.has(item._id) ? sum + (Number(item.payableAmount) || 0) : sum),
        0
      ),
    [data, selectedIds]
  );

  // Drives which bulk action the toolbar below offers. When every selected
  // row is already queued (e.g. the whole selection came from the "Under
  // Process" filter), "Mark as Under Process" would be a guaranteed no-op —
  // so the toolbar offers "Revert to Waiting" instead. A mixed selection
  // still defaults to "Mark as Under Process", same as before this check
  // existed - it only reversed the flag on the not-yet-queued rows.
  const selectedAllQueued = useMemo(() => {
    const selectedItems = data.filter((item) => selectedIds.has(item._id));
    return selectedItems.length > 0 && selectedItems.every((item) => item.queuedForProcessing);
  }, [data, selectedIds]);

  // Helper functions
  const convertDate = (id) => {
    if (!id) return "-";
    const date = new Date(parseInt(id.substr(0, 8), 16) * 1000);
    return date.toLocaleDateString("en-IN");
  };

  const getPlatformNames = (platforms) => {
    if (!platforms || !Array.isArray(platforms)) return "-";
    return platforms.map((p) => p.name).join(" ");
  };

  // queuedForProcessing is a Waiting sub-state (see PaymentActions.jsx),
  // not its own status code - shown here as its own label/color so the
  // Status column reflects it at a glance instead of just saying "Waiting".
  const isUnderProcessRow = (status, queuedForProcessing) => status === "W" && queuedForProcessing;

  const getStatusText = (status, queuedForProcessing) => {
    if (isUnderProcessRow(status, queuedForProcessing)) return "Under Process";
    switch (status) {
      case "A":
        return "Applied";
      case "P":
        return "Pending";
      case "W":
        return "Waiting";
      case "S":
        return "Success";
      case "R":
        return "Rejected";
      case "F":
        return "Failed";
      default:
        return status || "-";
    }
  };

  // Days between a payable date that's already passed and today — null
  // when there's nothing to compare against (payableDate defaults to null
  // for any PaymentLink never routed through the payment queue). Clamped
  // at 0 rather than going negative, since the Overdue filter this column
  // only appears under already guarantees payableDate < now server-side.
  const getDaysOverdue = (payableDate) => {
    if (!payableDate) return null;
    const diffMs = Date.now() - new Date(payableDate).getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  };

  const getStatusColor = (status, queuedForProcessing) => {
    if (isUnderProcessRow(status, queuedForProcessing)) return "#fd7e14";
    switch (status) {
      case "A":
        return "#FFC107";
      case "P":
        return "#0D6EFD";
      case "W":
        return "#0DCAF0";
      case "S":
        return "#198754";
      case "R":
        return "#dc3545";
      case "F":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedItem(null);
  };

  const stylingTable = {
    fontWeight: 700,
    fontSize: "11px",
    whiteSpace: "nowrap"
  };

  if (loading) {
    return (
      <Card sx={{ boxShadow: 0, p: 3 }}>
        <Box textAlign="center">
          <Typography variant="body1" color="textSecondary">
            Loading...
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ boxShadow: 0 }}>
        {isSuperUser && selectedIds.size > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              backgroundColor: "#fff3cd",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="body2">{selectedIds.size} selected</Typography>
            {selectedAllQueued ? (
              <Button
                size="small"
                variant="contained"
                color="info"
                onClick={() => onRevertToWaiting(Array.from(selectedIds))}
              >
                Revert to Waiting
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => onMarkUnderProcess(Array.from(selectedIds))}
              >
                Mark as Under Process
              </Button>
            )}
          </Box>
        )}
        {data.length === 0 ? (
          <Box textAlign="center" p={4}>
            <Typography variant="body1" color="textSecondary">
              No records found
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 160px)"
            }}
          >
            <Table
              aria-label="simple table"
              sx={{
                '& .MuiTableCell-root': {
                  padding: '4px 8px',
                  borderRight: 1,
                  borderRightColor: '#c0cacf'
                },
              }}
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  {/* Selection only powers the super-admin-only bulk toolbar
                      above, so it's hidden from everyone else rather than
                      showing a checkbox column that does nothing for them. */}
                  {isSuperUser && (
                    <TableCell sx={stylingTable} align="center" padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleSelectAll}
                        disabled={waitingItems.length === 0}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={stylingTable}>Date</TableCell>
                  <TableCell sx={stylingTable}>Payment</TableCell>
                  <TableCell sx={stylingTable}>Campaign</TableCell>
                  <TableCell sx={stylingTable}>Platform</TableCell>
                  <TableCell sx={stylingTable}>Influencer</TableCell>
                  <TableCell sx={stylingTable}>POC</TableCell>
                  <TableCell sx={stylingTable}>Total Amount</TableCell>
                  <TableCell sx={stylingTable}>GST Amount</TableCell>
                  <TableCell sx={stylingTable} align="center">%</TableCell>
                  <TableCell sx={stylingTable}>
                    Payable Amount
                    {isSuperUser && selectedPayableTotal > 0 && (
                      <Box component="span" sx={{ display: "block", color: "#198754" }}>
                        ₹{selectedPayableTotal.toLocaleString("en-IN")}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={stylingTable} align="center">Remarks</TableCell>
                  <TableCell sx={stylingTable} align="center">Status</TableCell>
                  {overdueOnly && (
                    <TableCell sx={stylingTable} align="center">Overdue By</TableCell>
                  )}
                  <TableCell sx={stylingTable} align="center">Action</TableCell>
                  <TableCell sx={stylingTable} align="center">SS</TableCell>
                </TableRow>
              </TableHead>
              {/* {console.time("map")} */}
              <TableBody>
                {data.map((item, index) => (
                  <PaymentTableRow
                    key={item._id || index}
                    item={item}
                    onView={handleView}
                    convertDate={convertDate}
                    getPlatformNames={getPlatformNames}
                    getStatusColor={getStatusColor}
                    getStatusText={getStatusText}
                    onCopyLink={onLinkCopy}
                    onDelete={onDelete}
                    onApprove={onApprove}
                    isAdmin={isAdmin}
                    showOverdueColumn={overdueOnly}
                    getDaysOverdue={getDaysOverdue}
                    selected={selectedIds.has(item._id)}
                    onToggleSelect={toggleSelect}
                    isSuperUser={isSuperUser}
                    onMarkUnderProcess={onMarkUnderProcess}
                    onRevertToWaiting={onRevertToWaiting}
                  />
                ))}
              </TableBody>
              {/* {console.timeEnd("map")} */}
            </Table>
          </TableContainer>
        )}
      </Card>

      <PaymentDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        selectedItem={selectedItem}
        getPlatformNames={getPlatformNames}
      />
    </>
  );
}